import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { ThumbnailConstants } from '../shared/thumbnail.constants';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { ContentType } from '../story/models/content-type.enum';
import { StoryChapterThumbnail } from '../story/models/story-chapter-thumbnail.model';
import { get } from 'lodash';

const thumbnailServiceBaseURL = '/ws/thumbs';
const thumbnailServiceCopyURL = '/ws/thumbs/copy';
const thumbnailServiceOptions =  {
  headers: new HttpHeaders().set('Content-Type', 'application/json'),
  responseType: 'text'
};

declare const localBaseDirectory: string;

@Injectable({
  providedIn: 'root'
})
export class ModulesThumbnailService {

  constructor(
    private http: HttpClient
  ) { }
  
  getStoryIndexThumbnail(storyId: string, size: string = 'large') {
    const directory = (typeof localBaseDirectory === 'undefined') ? '' : localBaseDirectory;
    return `${directory}thumbs/modules/${storyId}/${size}/index.jpg`;
  }

  saveStoryThumbnails(
    { storyId, storyType, thumbnails, chapterIdMap, indexId, deleteUnused = false }:
    { storyId: string;
      storyType: ContentType;
      thumbnails: Map<string, StoryChapterThumbnail>;
      chapterIdMap: { [key: string]: string; };
      indexId: string; 
      deleteUnused?: boolean; 
    }
  ) {
    /* Thumbnail Data
    The thumbnails are specific to the currently loaded content.
    Changes made to the thumbnais may or may not be replacements for the currently loaded content.
    When current thumbnails are meant to be "saved-as", we need to have a map of the target ids to the current ids.
    */
      
    // post/copy thumbnails for all story chapters and story
    const thumbnailRequests$: Observable<any>[] = [];
    const usedSourceIds = {};
  
    Object.entries(chapterIdMap)
      .forEach(([chapterId, thumbId]) => {
        const sourceThumbnail = thumbnails.get(thumbId); // the thumbnail object
        usedSourceIds[thumbId] = true; // track as part of thumbnail cleanup

        const targetPath = this.createThumbnailPath(storyType, storyId, chapterId);
        const snapsnot = get(sourceThumbnail, 'snapshot');
        
        if (snapsnot) {
          thumbnailRequests$.push(this.postThumbnail(sourceThumbnail.snapshot, targetPath));
          if (chapterId === indexId) {
            const indexPath = this.createThumbnailPath(storyType, storyId);
            thumbnailRequests$.push(this.postThumbnail(sourceThumbnail.snapshot, indexPath));
          }
        } else {
          // add copy request for existing story, chapter thumbnail, if it exists
          const largeUrl = get(sourceThumbnail, 'urls.large');
          if (largeUrl) {
            const sourcePath = largeUrl.split('/thumbs/')[1];
            thumbnailRequests$.push(this.copyThumbnail(sourcePath, targetPath));
            if (chapterId === indexId) {
              const indexPath = this.createThumbnailPath(storyType, storyId);
              thumbnailRequests$.push(this.copyThumbnail(sourcePath, indexPath));
            }
          }
        }
      });

    // delete any unused thumnbnails on the server (when we are replacing an existing story's thumbnails)
    if (deleteUnused) {
      Object.keys(thumbnails)
        .filter(sourceChapterId => !(sourceChapterId in usedSourceIds))
        .forEach(sourceChapterId => {
          const deletePath = thumbnails.get(sourceChapterId).urls.large.split('/thumbs/')[1];
          thumbnailRequests$.push(this.deleteThumbnail(deletePath));
        });
    }

    return thumbnailRequests$;
  }

  // create the thumbnail location (relative to content)
  private createThumbnailPath(storyType: ContentType, storyId: string, chapterId?: string): string {
    // storyId: preview/test.json

    // Sanitize story ID
    storyId = storyId.replace('.json', '');
    // storyId: preview/test

    // Determine which thumbnail service to use
    const thumbType = (storyType === ContentType.Bookmark) ? 'bookmarks' : 'modules';
    let imageName = `${storyId}/large`;
    // imageName: preview/test/large

    if (!!chapterId) {
      imageName = `${imageName}/chapters/${chapterId}.jpg`;

      // imageName: preview/test/large/chapter-9472aead-8be3-43b9-9ae8-6821b4cfcccd.jpg
    } else {
      imageName = `${imageName}/index.jpg`;
    }
    
    return [thumbType, imageName].join('/');
  }

  // request to server to save a thumbnail
  private postThumbnail(sourceURL: string, targetPath: string): Observable<any> {
    const { width, height } = ThumbnailConstants.medium;
    const image = new Image();
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    image.src = sourceURL;

    return new Observable((observer) => {
      image.onerror = () => {
        observer.next();
        observer.complete();
      };
      
      image.onload = () => {
        // transfer image to canvas
        context.drawImage(image, 0, 0); 

        // convert to data url
        const dataURL = canvas.toDataURL('image/jpeg', 1.0);

        // issue post
        const dstURL = [thumbnailServiceBaseURL, targetPath].join('/');
        this.http.post(dstURL, {image: dataURL}, thumbnailServiceOptions as Object).subscribe(() => {
          observer.next();
          observer.complete();
        });
      };
    });
  }

  // request to delete thumbnail from server
  private deleteThumbnail(targetPath: string): Observable<any> {
    const dstURL = [thumbnailServiceBaseURL, targetPath].join('/');
    return new Observable((observer) => {
      const request = new Request(dstURL, {method: 'DELETE'});
      fetch(request).finally(() => { 
        observer.next();
        observer.complete();
      });
    });
  }

  // request to server to copy a thumbnail from one location to another
  private copyThumbnail(sourcePath: string, targetPath: string) {
    return new Observable((observer) => {
      this.http.post(thumbnailServiceCopyURL, {src: sourcePath, dst: targetPath}, thumbnailServiceOptions as Object).subscribe((r) => {
        observer.next();
        observer.complete();
      });
    });
  }
}
